import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchStudyMaterials,
  updateStudyMaterialStatus,
  deleteStudyMaterial,
  insertStudyMaterial,
  editStudyMaterial,
} from "../../../store/studyMaterialsSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";

const StudyMaterials = () => {
  const dispatch = useDispatch();

  // Pull data from Redux
  const { studyMaterials = [], loading, error } = useSelector(
    (state) => state.studyMaterials
  );
  const { ictData = [] } = useSelector((state) => state.ui);

  // Filters
  const [searchSMName, setSearchSMName] = useState("");
  const [filterSMType, setFilterSMType] = useState("");
  const [filterICTName, setFilterICTName] = useState("");

  // Popups
  const [isInsertPopupOpen, setInsertPopupOpen] = useState(false);
  const [isEditPopupOpen, setEditPopupOpen] = useState(false);

  // Form states (insert & edit)
  const [newMaterial, setNewMaterial] = useState({
    ictId_id: "",
    SMName: "",
    SMSrc: "",
    SMType: "",
    SMStatus: "A",
  });
  const [editMaterial, setEditMaterial] = useState(null);

  useEffect(() => {
    dispatch(fetchStudyMaterials());
    dispatch(fetchICTData());
  }, [dispatch]);

  // Unique filter options from the data
  const uniqueSMTypes = Array.from(
    new Set(studyMaterials.map((material) => material.SMType).filter(Boolean))
  );
  const uniqueICTNames = ictData.map((ict) => ict.ictName);

  const handleSearchSMName = (e) => setSearchSMName(e.target.value);

  // Filtered results
  const filteredMaterials = studyMaterials.filter((material) => {
    // Name filter
    const nameMatches = material.SMName
      .toLowerCase()
      .includes(searchSMName.toLowerCase());

    // SMType filter
    const typeMatches = filterSMType
      ? material.SMType === filterSMType
      : true;

    // ICT filter
    const ictName =
      ictData.find((ict) => ict.id === material.ictId_id)?.ictName || "";
    const ictMatches = filterICTName
      ? ictName.toLowerCase() === filterICTName.toLowerCase()
      : true;

    return nameMatches && typeMatches && ictMatches;
  });

  // Toggle Status
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "A" ? "D" : "A";
    try {
      await dispatch(
        updateStudyMaterialStatus({ id, SMStatus: newStatus })
      ).unwrap();
      toast.success(
        `Status updated to ${newStatus === "A" ? "Active" : "Inactive"}`
      );
    } catch (err) {
      // Provide details if the server returned an error object
      const errMsg =
        typeof err === "object" ? JSON.stringify(err) : String(err);
      toast.error(`Failed to update status: ${errMsg}`);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await dispatch(deleteStudyMaterial(id)).unwrap();
        toast.success("Study material deleted successfully");
      } catch (err) {
        const errMsg =
          typeof err === "object" ? JSON.stringify(err) : String(err);
        toast.error(`Failed to delete study material: ${errMsg}`);
      }
    }
  };

  // Insert
  const handleInsert = async (e) => {
    e.preventDefault();
    if (!newMaterial.ictId_id || !newMaterial.SMName || !newMaterial.SMSrc) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await dispatch(insertStudyMaterial(newMaterial)).unwrap();
      toast.success("Study material inserted successfully");
      setInsertPopupOpen(false);
      setNewMaterial({
        ictId_id: "",
        SMName: "",
        SMSrc: "",
        SMType: "",
        SMStatus: "A",
      });
    } catch (err) {
      const errMsg =
        typeof err === "object" ? JSON.stringify(err) : String(err);
      toast.error(`Failed to insert study material: ${errMsg}`);
    }
  };

  // Edit
  const handleEdit = async (e) => {
    e.preventDefault();
    // We must have editMaterial.id to do a PUT request
    if (!editMaterial?.id) {
      toast.error("No valid record to update");
      return;
    }
    try {
      await dispatch(editStudyMaterial(editMaterial)).unwrap();
      toast.success("Study material updated successfully");
      setEditPopupOpen(false);
      setEditMaterial(null);
    } catch (err) {
      const errMsg =
        typeof err === "object" ? JSON.stringify(err) : String(err);
      toast.error(`Failed to update study material: ${errMsg}`);
    }
  };

  // Convert any object error to string for display
  const renderError = () => {
    if (!error) return null;
    const msg = typeof error === "object" ? JSON.stringify(error) : error;
    return <div className="error">Error: {msg}</div>;
  };

  return (
    <div className="container">
      <div className="header-container">
        <h2>Study Materials</h2>
        <button className="add-lead-btn" onClick={() => setInsertPopupOpen(true)}>
          <i className="fa fa-plus"></i>
        </button>
      </div>

      {/* Filter Section */}
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by Material Name..."
          value={searchSMName}
          onChange={handleSearchSMName}
          className="filter-input"
        />

        <select
          value={filterSMType}
          onChange={(e) => setFilterSMType(e.target.value)}
          className="filter-input"
        >
          <option value="">All Material Types</option>
          {uniqueSMTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <select
          value={filterICTName}
          onChange={(e) => setFilterICTName(e.target.value)}
          className="filter-input"
        >
          <option value="">All ICTs</option>
          {uniqueICTNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Loading & Error */}
      {loading && <div className="loading">Loading study materials...</div>}
      {renderError()}

      {!loading && !error && (
        <table className="table">
          <thead>
            <tr>
              <th>#</th>
              <th>ICT</th>
              <th>Material Name</th>
              <th>Source</th>
              <th>Type</th>
              <th>Status</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMaterials.map((material, index) => {
              // Lookup ICT name
              const ictName =
                ictData.find((ict) => ict.id === material.ictId_id)?.ictName ||
                "N/A";
              return (
                <tr key={material.id}>
                  <td>{index + 1}</td>
                  <td>{ictName}</td>
                  <td>{material.SMName}</td>
                  <td>
                    <a href={material.SMSrc} target="_blank" rel="noopener noreferrer">
                      {material.SMSrc}
                    </a>
                  </td>
                  <td>{material.SMType || "N/A"}</td>
                  <td>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={material.SMStatus === "A"}
                        onChange={() =>
                          handleStatusToggle(material.id, material.SMStatus)
                        }
                      />
                      <span className="slider round"></span>
                    </label>
                  </td>
                  <td>
                    {material.created
                      ? new Date(material.created).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    {material.updated
                      ? new Date(material.updated).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    <button
                      className="icon-btn update-btn fa fa-edit"
                      onClick={() => {
                        setEditMaterial(material);
                        setEditPopupOpen(true);
                      }}
                    ></button>
                    <button
                      className="icon-btn delete-btn fa fa-trash"
                      onClick={() => handleDelete(material.id)}
                    ></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Insert Popup */}
      {isInsertPopupOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Insert New Study Material</h3>
            <form onSubmit={handleInsert}>
              <div className="form-group">
                <label>Select Course</label>
                <select
                  name="ictId_id"
                  value={newMaterial.ictId_id}
                  onChange={(e) =>
                    setNewMaterial({ ...newMaterial, ictId_id: e.target.value })
                  }
                  required
                >
                  <option value="">Select Course</option>
                  {ictData.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.ictName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Material Name</label>
                <input
                  type="text"
                  name="SMName"
                  value={newMaterial.SMName}
                  onChange={(e) =>
                    setNewMaterial({ ...newMaterial, SMName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Material Type</label>
                <select
                  name="SMType"
                  value={newMaterial.SMType}
                  onChange={(e) =>
                    setNewMaterial({ ...newMaterial, SMType: e.target.value })
                  }
                  required
                >
                  {/* 'Courseware Book','Practice Lab','Course Outline','Visual Guide' */}
                  <option value="">Select Type</option>
                  <option value="Course Outline">Course Outline</option>
                  <option value="Courseware Book">Courseware Book</option>
                  <option value="Practice Lab">Practice Lab</option>
                  <option value="Visual Guide">Visual Guide (PPT)</option>
                  <option value="Video">Video</option>
                </select>
              </div>
              <div className="form-group">
                <label>Recording Source URL</label>
                <input
                  type="text"
                  name="SMSrc"
                  value={newMaterial.SMSrc}
                  onChange={(e) =>
                    setNewMaterial({ ...newMaterial, SMSrc: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn fa fa-check-square"></button>
                <button
                  type="button"
                  className="cancel-btn fa fa-close"
                  onClick={() => setInsertPopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Popup */}
      {isEditPopupOpen && editMaterial && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Edit Study Material</h3>
            <form onSubmit={handleEdit}>
              {/* We require the ICT as well, or else the PUT might fail if the server requires it */}
              <div className="form-group">
                <label>Select Course</label>
                <select
                  name="ictId_id"
                  value={editMaterial.ictId_id || ""}
                  onChange={(e) =>
                    setEditMaterial({ ...editMaterial, ictId_id: e.target.value })
                  }
                  required
                >
                  <option value="">Select Course</option>
                  {ictData.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.ictName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Material Name</label>
                <input
                  type="text"
                  name="SMName"
                  value={editMaterial.SMName}
                  onChange={(e) =>
                    setEditMaterial({ ...editMaterial, SMName: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Material Type</label>
                <select
                  name="SMType"
                  value={editMaterial.SMType}
                  onChange={(e) =>
                    setEditMaterial({ ...editMaterial, SMType: e.target.value })
                  }
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Course Outline">Course Outline</option>
                  <option value="Courseware Book">Courseware Book</option>
                  <option value="Practice Lab">Practice Lab</option>
                  <option value="Visual Guide">Visual Guide (PPT)</option>
                  <option value="Video">Video</option>
                </select>
              </div>
              <div className="form-group">
                <label>Recording Source URL</label>
                <input
                  type="text"
                  name="SMSrc"
                  value={editMaterial.SMSrc}
                  onChange={(e) =>
                    setEditMaterial({ ...editMaterial, SMSrc: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="submit-btn fa fa-edit"></button>
                <button
                  type="button"
                  className="cancel-btn fa fa-close"
                  onClick={() => setEditPopupOpen(false)}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default StudyMaterials;
